/**
 * What a member of staff may do in the booking system, in FastAPI's words.
 *
 * **This vocabulary is not ours.** Every name here is defined by the
 * integration contract and understood on the far side; the Hub's job is to say
 * truthfully which of them a person holds. A name FastAPI does not recognise is
 * at best ignored and at worst a rejected token, and neither failure says which
 * word was wrong — so the list is written down once, here, and everything that
 * grants, stores, displays or signs a capability reads it from this file.
 *
 * **Most of them are all-provider escalations.** What they usually lift is the
 * boundary of *whose* lessons, customers or money somebody can reach, which is
 * why they are granted one at a time by an owner rather than implied by a job
 * title. `lesson_blocks.create.own_students` is retained only for v1 token
 * compatibility: ordinary tutor booking and standard block creation are
 * inherent own-scope operations and do not depend on that claim.
 * `lesson_blocks.manage.own_students` remains a narrow grant for exceptional
 * financial-management operations within a tutor's own student scope.
 *
 * **Two families are narrower than that, and the descriptions say so.** The
 * eight `reports.*` names open a report at all rather than open it over
 * everybody: a tutor holding one still reads only their own provider's figures,
 * and it is `reports.organisation.read` alone that widens them to the school.
 * `lesson_requests.manage` is the same shape — FastAPI keeps matching the
 * request's provider to the tutor's, so it grants the queue and not the right
 * to answer a colleague's request. Granting one of these is therefore a smaller
 * decision than granting `appointments.manage.all`, and an owner reading the
 * screen should be able to tell which sort they are making.
 */

/** Every capability FastAPI recognises, per the integration contract. */
export const STAFF_CAPABILITIES = [
  "appointments.read.all",
  "appointments.manage.all",
  "automations.manage",
  "booking_configuration.manage",
  "customers.delete",
  "customers.manage",
  "customers.read.all",
  "lesson_blocks.create.own_students",
  "lesson_blocks.manage.all",
  "lesson_blocks.manage.own_students",
  "lesson_requests.manage",
  "lesson_requests.manage.all",
  "payments.manage",
  "recurrences.read.all",
  "reports.attendance.read",
  "reports.credits.read",
  "reports.earnings.read",
  "reports.operations.read",
  "reports.organisation.read",
  "reports.payments.read",
  "reports.students.read",
  "reports.utilisation.read",
  "schedules.read.all",
  "schedules.manage.all",
  "staff.manage",
  "school_day.manage",
] as const;

export type StaffCapability = (typeof STAFF_CAPABILITIES)[number];

/**
 * What a staff account holds the first time an invitation makes it a teacher,
 * before an owner has decided anything specific about it.
 *
 * The day-one default used to be nothing: an accepted invitation granted the
 * `tutor` role and a profile, and every capability after that was a separate
 * grant. This is the set the school wants a new member of staff to have without
 * waiting for that step — read-only visibility of the whole diary plus the
 * separately protected financial workflows around one's own students.
 * Ordinary booking and standard five-credit block creation are inherent own
 * scope and therefore need no default capability.
 *
 * What each one reaches:
 *
 *  - `appointments.read.all` is the only all-provider name here, and it is
 *    read-only: see every lesson in the diary, change none that is not yours.
 *  - `lesson_blocks.manage.own_students` permits the exceptional lesson-block
 *    work for assigned students: goodwill and carried-forward credit, other
 *    block sizes, balance corrections, refunds and expiry. Standard add/close
 *    operations do not depend on it.
 *  - `lesson_requests.manage` stays matched to the holder's own provider: it
 *    grants their pay-on-the-day queue, not a colleague's.
 *  - `payments.manage` records a payment or refund as received, and the
 *    appointment backend keeps the tutor's provider boundary on those writes.
 *
 * `lesson_blocks.create.own_students` is intentionally absent: it is accepted
 * in old tokens for compatibility but maps to no authority. Re-adding it here
 * would misleadingly make an inherent right look revocable.
 *
 * `customers.manage` was considered and left out. WMF-SIte authorises customer
 * create and update on that capability alone, with no teaching-assignment or
 * ownership seam, so it is an all-provider write escalation — an owner grants
 * it deliberately, per person, the same as `appointments.manage.all`.
 *
 * Applied once, and only when this acceptance is what first granted the `tutor`
 * role. An account that was already staff — a returning leaver, a profile
 * adopted from the booking provider — keeps whatever an owner has since curated;
 * the default is not re-asserted over a deliberate removal. See
 * `acceptInvitation`.
 */
export const DEFAULT_STAFF_CAPABILITIES = [
  "appointments.read.all",
  "lesson_blocks.manage.own_students",
  "lesson_requests.manage",
  "payments.manage",
] as const satisfies readonly StaffCapability[];

/**
 * What each one means to the owner deciding whether to grant it.
 *
 * Written as what the person can then *do*, and saying "anybody's" out loud
 * wherever that is the difference — the whole point of a capability here is
 * that it reaches past the holder's own students, and a description that left
 * that implicit would be describing something harmless that this is not.
 */
export const CAPABILITY_DESCRIPTIONS: Record<StaffCapability, string> = {
  "appointments.read.all": "See every lesson in the diary, not only their own.",
  "appointments.manage.all": "Book, move and cancel lessons for any tutor.",
  "automations.manage": "Change the automatic messages and jobs the booking system runs.",
  "booking_configuration.manage":
    "Change how private booking is set up for the whole school.",
  "customers.delete":
    "Delete a customer outright, for anybody. Kept separate from editing one because it cannot be undone.",
  "customers.manage": "Create and edit customer records in the booking system.",
  "customers.read.all": "Look up any customer in the booking system.",
  "lesson_blocks.create.own_students":
    "Compatibility-only legacy claim. Tutors can already book and add standard blocks for their own students without it.",
  "lesson_blocks.manage.all": "Issue, adjust and withdraw blocks of lessons for anybody.",
  "lesson_blocks.manage.own_students":
    "Give goodwill and carried-forward credit, sell other block sizes, correct balances, and record refunds and expiry changes — only for students assigned to them.",
  "lesson_requests.manage":
    "Approve and reject requests for lessons to be paid for on the day. Still their own lessons only, unless they own the school.",
  "lesson_requests.manage.all":
    "Answer lesson requests for every tutor, not only their own students.",
  "payments.manage":
    "Record payments and refunds, and mark their own lessons as paid for. An office account with no lessons of its own also needs “Book, move and cancel lessons for any tutor” to record payments for the school.",
  "recurrences.read.all": "See every recurring lesson, not only their own.",
  "reports.attendance.read": "Open the attendance report — lessons taught, missed and cancelled.",
  "reports.credits.read": "Open the lesson-block report — credits outstanding, expiring and owed.",
  "reports.earnings.read": "Open the earnings report — confirmed takings before fees and tax.",
  "reports.operations.read": "Open the list of bookings, coupons and payments needing attention.",
  "reports.organisation.read":
    "Read every report for the whole school. Without this, a report shows only their own lessons.",
  "reports.payments.read": "Open the payments report — what has been taken, refunded and is still owed.",
  "reports.students.read": "Open the student report — who is active, who is new and who has stopped booking.",
  "reports.utilisation.read": "Open the timetable report — hours taught, teaching days and gaps.",
  "schedules.read.all": "See every tutor's working hours and time off.",
  "schedules.manage.all":
    "Block out and free up time on any tutor's calendar, not only their own.",
  "staff.manage": "Add, change and remove staff in the booking system.",
  "school_day.manage": "Publish opaque School availability commitments for Amelia provider #1.",
};

const KNOWN = new Set<string>(STAFF_CAPABILITIES);

export function isStaffCapability(value: string): value is StaffCapability {
  return KNOWN.has(value);
}

/**
 * Grants whose only meaning is "my own provider's lessons and pupils".
 *
 * Every description above says so: `lesson_requests.manage` grants the
 * holder's own pay-on-the-day queue and never a colleague's, the two
 * `*.own_students` names are about the holder's own pupils, and the Standard
 * tutor preset records `payments.manage` "within the existing provider scope".
 * An account with no Amelia provider has no own lessons, so these grants mean
 * nothing for it — and FastAPI reads a missing provider as "no provider
 * filter", which turned them into organisation-wide authority for every newly
 * invited tutor whose booking-system link had not yet been made (2026-10-16
 * audit, SEC-01).
 */
export const OWN_PROVIDER_ONLY_CAPABILITIES: ReadonlySet<StaffCapability> = new Set([
  "lesson_blocks.create.own_students",
  "lesson_blocks.manage.own_students",
  "lesson_requests.manage",
  "payments.manage",
]);

/**
 * What a non-owner with no Amelia provider may assert.
 *
 * The own-provider-only grants are dropped, with one exception decided on
 * 16 October 2026: an office account — somebody who does not teach and records
 * payments for the whole school — is one an owner has explicitly allowed to
 * manage every tutor's lessons. Holding `appointments.manage.all` is what
 * makes `payments.manage` school-wide for an account without a provider; on
 * its own it no longer does. Answering requests works the same way: an office
 * account that holds `lesson_requests.manage.all` keeps the queue grant beside
 * it, and without that widening an unlinked account has no queue.
 */
const KEPT_WITH_WIDENING: ReadonlyMap<StaffCapability, StaffCapability> = new Map([
  ["payments.manage", "appointments.manage.all"],
  ["lesson_requests.manage", "lesson_requests.manage.all"],
]);

export function capabilitiesWithoutProvider(
  held: readonly StaffCapability[],
): StaffCapability[] {
  return held.filter((capability) => {
    if (!OWN_PROVIDER_ONLY_CAPABILITIES.has(capability)) return true;
    const widening = KEPT_WITH_WIDENING.get(capability);
    return widening !== undefined && held.includes(widening);
  });
}

/**
 * Whether this account may open a pay-on-the-day request queue at all: its own
 * (`lesson_requests.manage`) or everybody's (`lesson_requests.manage.all`).
 * Either grant opens the screen; FastAPI decides which requests it returns.
 */
export function canOpenLessonRequests(held: ReadonlySet<string>): boolean {
  return held.has("lesson_requests.manage") || held.has("lesson_requests.manage.all");
}

/**
 * A stored set, made fit to send.
 *
 * Three things happen here, and each answers a way the database can disagree
 * with the contract. Unknown names are **dropped rather than passed on**: rows
 * outlive deployments, so a capability FastAPI later renames or retires would
 * otherwise keep being asserted at it long after it meant anything. Duplicates
 * collapse. And the order is the canonical one rather than whatever the rows
 * came back in, so two tokens for the same person are the same token — which is
 * what makes a claim worth comparing in a test or a log.
 */
export function normaliseCapabilities(values: readonly string[]): StaffCapability[] {
  const held = new Set(values);
  return STAFF_CAPABILITIES.filter((capability) => held.has(capability));
}

/** Why granting to this person would not mean anything. */
export type CapabilityRefusal =
  /** They have left. Their grants are dormant until somebody puts them back. */
  | "left"
  /** No staff role, so nothing here would ever be sent about them. */
  | "not-staff";

/** As much of somebody as this decision needs. */
export interface CapabilityHolder {
  roles: readonly string[];
  /** When they stopped teaching here, or null while they still do. */
  leftAt: Date | null;
}

/**
 * Whether a grant to this person would be worth anything.
 *
 * Pure and tested on its own, in the same shape as the invitation rules, so
 * that "who can be granted what" is a rule somebody can read rather than three
 * branches in the middle of a route.
 *
 * A current owner or tutor is staff for this purpose. Owners may now hold rows
 * even though the temporary compatibility policy still makes their effective
 * authority universal. That permits the reviewed authority manifest to be
 * pre-provisioned and verified before the owner-all cutover.
 */
export function refuseCapabilityGrant(holder: CapabilityHolder): CapabilityRefusal | null {
  if (holder.leftAt !== null) return "left";
  if (!holder.roles.includes("owner") && !holder.roles.includes("tutor")) return "not-staff";
  return null;
}

/** What to tell the owner who tried. */
export function describeCapabilityRefusal(reason: CapabilityRefusal): string {
  switch (reason) {
    case "left":
      return "They no longer teach here. Put them back first, and what they held returns with them.";
    case "not-staff":
      return "Only staff can hold capabilities in the booking system.";
  }
}
